import React, { useEffect, useState, useRef, useMemo } from 'react'
import Spinner from 'react-spinkit'
import classnames from 'classnames'
import useDimensions from "react-use-dimensions";
import { scaleThreshold, scaleLinear } from 'd3-scale'
import { geoPath, geoAlbersUsa } from 'd3-geo'
import { group } from 'd3-array'
import { format } from 'd3-format';
import startCase from 'lodash/startCase'

import TimelapseBarGraph from './TimelapseBarGraph'
import TimelapseControls from './TimelapseControls'

import { getText } from '../languages';
import Tooltip from '../WorldMap/Tooltip'
import { useParams, useHistory } from 'react-router-dom'
import './style.scss'
import exportEmbedIcon from '../images/export_icon.png'
import Export from '../Export/'

const width = 960
const height = 500
const directionality = 'ltr';
const language = 'en';
const isWorld = false;

export const casesColors = [
  '#FFFBDC',
  '#FFEDB8',
  '#FFDD94',
  '#FBBF69',
  '#F5A057',
  '#ED6942',
  '#D73534',
  '#BF1B3E',
  '#952742',
  '#641A2C',
]

export const deathsColors = [
  '#c9dee0',
  '#a0b0b1',
  '#757f80',
  '#4d5353',
  '#272828',
]

export const perCapitaMultiplier = 100 * 1000; // we now are hardcoding 100,000 into translations strings, change this at your peril
const populationKey = 'POPESTIMATE2019'

const perCapitaValues = {}
const perCapitaLabelMaker = (value, decimalPlaces = 1, language='en') => {
  const key = `${value}`
  if (!perCapitaValues[key]) {
    if (value === null) {
      perCapitaValues[key] = 'No Data'
    } else {
      perCapitaValues[key] = (value*perCapitaMultiplier).toLocaleString()
    }
  }
  return perCapitaValues[key]
}

function mapToNest(map) {
  return Array.from(map, ([key, values]) => ({key, values}));
}


const zeroPadDayMonth = format('0=2');
function formatDateString(date) {
  return `${date.getFullYear()}-${zeroPadDayMonth(date.getMonth() + 1)}-${zeroPadDayMonth(date.getDate())}`;
}

function formatDateLabel(date) {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()-2000}`;

}
const newAverageKeys = {
  cases: 'new_cases_14dayAverage',
  deaths: 'new_deaths_14dayAverage',
}

function Timelapse(props) {
  const {windowWidth, embedParams} = props
  const [mapContainerRef, mapContainerDimensions] = useDimensions();

  let scale = mapContainerDimensions.width / width || 1;
  const mapContainerHeight = Math.min(height * scale, window.innerHeight - 220);
  scale = mapContainerHeight / height;

  const canvasRef = useRef()

  const rafId = useRef()
  const [floatDateIndex, setDateIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [casesDeathsMetricKey, setCasesDeathsMetricKey] = useState('cases')
  const [hoveredFips, setHoveredFips] = useState(null)

  const [playbackSpeed, setPlaybackSpeed] = useState(0.5)
  const [showExport, setShowExport] = useState(false);

  const { selectedStateId, selectedStateAbbr } = props
  const history = useHistory()

  const selectedMetric = { metricKey: casesDeathsMetricKey };
  const barGraphColor = useMemo(() => casesDeathsMetricKey === 'cases' ? '#d1b1b7' : '#c9dee0', [casesDeathsMetricKey]);
  const usPopulation = props.populations['-1'].POPESTIMATE2019;

  let rightColumnWidthWithPadding = 240
  let rightColumnContentWidth = rightColumnWidthWithPadding - (4 * 8)
  let leftColumnWidth = windowWidth - rightColumnContentWidth
  const singleColumnMode = windowWidth < 800
  let mainColumnWidth =  `calc(100vw - ${rightColumnWidthWithPadding}px)`
  if (singleColumnMode) {
    leftColumnWidth = windowWidth
    rightColumnWidthWithPadding = windowWidth
    mainColumnWidth = '100vw'
  }


  rightColumnContentWidth = rightColumnWidthWithPadding - (4 * 8)
  const topControlsWidth = 220
  const dateSliderWidth = singleColumnMode ? windowWidth : windowWidth - topControlsWidth
  const barGraphHeight = rightColumnContentWidth / 1.6

  useEffect(() => {
    // eslint-disable-next-line no-undef
    if (gtag) {
      // eslint-disable-next-line no-undef
      gtag('event', 'page_view', {
        page_title: 'Timelapse',
        page_location: document.location.toString(),
        page_path: document.location.pathname,
      })
    }
  }, [])
  useEffect(() => {
    // console.log('useEffect')
    // console.log(props)

    // if (canvasRef.current ) {
      // rafId.current = requestAnimationFrame(animate)
    // }
    const animate = () => {
      setDateIndex(dateIndex => {
        let newDateIndex = dateIndex
        // console.log('newIndex', newDateIndex)
        setPaused(paused => {
          let newPaused = paused
          if (!paused) {
            const nextDateIndex = dateIndex + playbackSpeed
            if (nextDateIndex < props.dateSet.length) {
              newDateIndex = nextDateIndex
            } else {
              newDateIndex = props.dateSet.length - 1
              newPaused = true
            }
          } else {
            // console.log('paused')
          }
          return newPaused
        })
        return newDateIndex
      })
      rafId.current = requestAnimationFrame(animate)
    }
    rafId.current = requestAnimationFrame(animate)
    return () => {
      cancelAnimationFrame(rafId.current)
    }
  }, [props.dateSet.length, playbackSpeed])

  const dateIndex = ~~ floatDateIndex
  const { colorScale, casesColorScale, deathsColorScale } = useMemo(() => {

    let colorScale = null;

    const casesColorScale = scaleThreshold()
      .domain([25, 50, 75, 100, 150, 200, 250, 500, 1000].map(d => d / perCapitaMultiplier ))
      .range(casesColors) //10 colors, so need 9 thresholds

    const deathsColorScale = scaleThreshold()
      .domain([1, 5, 10, 50].map(d => d / perCapitaMultiplier ))
      .range(deathsColors)

    if (casesDeathsMetricKey === 'cases') {
      colorScale = casesColorScale
    } else if (casesDeathsMetricKey === 'deaths') {
      colorScale = deathsColorScale
    }

    const colors = colorScale.range();

    return { casesColorScale, deathsColorScale, colorScale, colors };
  }, [casesDeathsMetricKey]);

  const statDataByDay = useMemo(() => {
    console.log('update stat data');
    const dateStrings = props.dateSet.map(d => d.dateString)
    const id = selectedStateAbbr ? selectedStateAbbr : 'US'
    const baseStatData = props.regionDataByKey[id].data.filter(datum => dateStrings.includes(formatDateString(datum.date)));
    return props.dateSet.map((dateObj, dateIndex) => {
      const dayStatData = baseStatData.filter((datum, index) => index <= dateIndex);
      const twoWeeks = dayStatData.slice(-14);
      return {
        data: dayStatData,
        twoWeeks,
      };
    });
  }, [props.regionDataByKey, props.dateSet, selectedStateAbbr]);
  const statData = statDataByDay[dateIndex];
  // console.log(statData)


  let stats = []
  let cases = 0
  let deaths = 0;

  if (statData.data[dateIndex]) {
    stats =[
      { label: `Daily Confirmed ${startCase(casesDeathsMetricKey)}`, value: statData.data[dateIndex][`new_${casesDeathsMetricKey}`]},
      { label: `14-Day ${startCase(casesDeathsMetricKey)} Count `, value: statData.data[dateIndex][`new_${casesDeathsMetricKey}_14dayAverage`]},
      { label: `Total ${startCase(casesDeathsMetricKey)} To Date`, value: statData.data[dateIndex][casesDeathsMetricKey]},
    ]
    cases = statData.data[dateIndex].cases;
    deaths = statData.data[dateIndex].deaths
  }
  let countyPaths = null

  useMemo(() => {
    const projection = geoAlbersUsa()
    if (selectedStateId) {
      const state = props.states.filter(d => d.id === selectedStateId)[0]
      projection.fitExtent(
        [[50, 50], [width - 100, height - 100]],
        state
      )
    }
    const pathGen = geoPath(projection)
    const paths = {}
    console.log('calculate paths')
    props.counties.forEach(county => {
      const countyPath = pathGen(county)
      county.properties.pathString = countyPath
      paths[county.id] = countyPath
    })
    props.states.forEach(state => {
      const statePath = pathGen(state)
      state.properties.pathString = statePath
      paths[state.properties.STATE] = statePath
    })
  }, [props.counties, props.states, selectedStateId])

  const hoverCountyPath = county => event => {
    if (county === null) {
      setHoveredFips(null)
      return
    }

    const fips = {
      county,
      x: event.clientX,
      y: event.clientY
    }
    setHoveredFips(fips)

  }

  const clickCountyPath = county => event => {
    // const countyMetadata = props.populations[county.id]
    // const state = countyMetadata.stateAbbr
    const stateAbbr = county.properties.regionData.stateAbbr
    const stateId = county.properties.STATEFP
    if (stateId === selectedStateId) {
      history.replace('/timelapse')
    } else {
      history.replace(`/timelapse/${stateAbbr}`)
    }
  }
  if (props.counties && props.counties.length) {
    countyPaths = props.counties.map(county => {
      const path = county.properties.pathString
      const dataKey = props.dateSet[dateIndex].dateString
      let fill = '#ddd'
      const regionData = county.properties.regionData || props.regionsById[county.id]
      let population = county.properties.population
      if (selectedStateId && selectedStateId !== county.properties.STATEFP) {
        return <path key={county.id} />
      }
      if (!population) {
        const populationDatum = props.populations[county.id]
        population = populationDatum ? populationDatum[populationKey] : null;
      }
      if (regionData && population) {
        county.properties.regionData = regionData
        county.properties.population = population
        if (dataKey in regionData.dataMap) {
          const dayDatum  = regionData.dataMap[dataKey]
          const value = dayDatum[newAverageKeys[casesDeathsMetricKey]]
          const perCapita = value / population

          fill = perCapita === 0 ? '#fff' : (casesDeathsMetricKey === 'cases' ? casesColorScale : deathsColorScale)(perCapita)
        }
      }
      let stroke = null
      if (hoveredFips && hoveredFips.county.id === county.id) {
        stroke = 'black'
      }
      return (
        <path
          d={path}
          fill={fill}
          key={county.id}
          onMouseMove={hoverCountyPath(county)}
          onMouseOver={hoverCountyPath(county)}
          onMouseOut={hoverCountyPath(null)}
          onClick={clickCountyPath(county)}
          stroke={stroke}
        />
      )
    })
    countyPaths.push(...props.states.map(state => {
      const path = state.properties.pathString
      if (selectedStateId && state.properties.STATE !== selectedStateId) {
        return null
      }
      return <path d={path} fill='none' strokeOpacity="0.2" stroke='black' key={state.properties.STATE} />
    }))
  }

  const summaryByDay = useMemo(() => {
    console.log('update summary by day');
    return props.dateSet.map((date, dateIndex) => {
      const colors = colorScale.range();
      const regionsWithData = props.counties.map(county => {

          county.perCapita = null;
          const dataKey = props.dateSet[dateIndex].dateString
          // let fill = 'white'
          const regionData = county.properties.regionData || props.regionsById[county.id]
          const population = county.properties.population

          if (regionData && population) {
            const dayDatum  = regionData.dataMap[dataKey]

            if (dayDatum) {
              const value = dayDatum[newAverageKeys[casesDeathsMetricKey]]
              const perCapita = value / population
              county.perCapita = perCapita;
            }
          }
          return county;
        }).filter(county => county.perCapita !== null);
      const summaryData = mapToNest(group(
          regionsWithData,
          region => region.perCapita === 0 ? '#fff' : colorScale(region.perCapita),
        )).map(group => {
          group.index = colors.indexOf(group.key);
          group.category = colorScale.invertExtent(group.key);
          const decimalPlaces = 0;
          if (group.index === -1) {
            group.label = 0;
          } else if (group.index === 0) {
            group.label = casesDeathsMetricKey === 'cases' ? '' : '> 0';
          } else if (group.index === colors.length - 1) {
            group.label = `> ${perCapitaLabelMaker(group.category[0], decimalPlaces, language)}`;
          } else {
            group.label = perCapitaLabelMaker(group.category[0], decimalPlaces, language);
          }
          return group;
        }).sort((a, b) => a.index - b.index);

      const summaryWidth = 815;
      const outerWidth = summaryWidth;
      const scale = outerWidth / summaryWidth;
      const summaryHeight = 60;
      const groupHeight = 15;
      const minSegmentWidth = groupHeight * 1.5;
      const sectionHeight = 20;

      const totalGroupSize = summaryData.map(group => group.values.length).reduce((a, v) => a + v, 0);
      const summaryXScale = scaleLinear().domain([0, totalGroupSize]).range([minSegmentWidth, summaryWidth]);
      const falseTotalWidth = summaryData.map(group => summaryXScale(group.values.length)).reduce((a, v) => a + v, 0);
      const getGroupWidth =  group => (summaryXScale(group.values.length) / falseTotalWidth) * summaryWidth;
      let xOffset = 0;

      return (
        <div className='summary'>
          <svg width={width} height={summaryHeight * scale}>
            <g transform={`translate(${(width - outerWidth) / 2}, 5)`}>
              <g transform={`scale(${scale})`}>
                <g transform={`translate(${minSegmentWidth}, 0)`}>
                  {
                    // <rect width={summaryWidth} height={summaryHeight} fill='white' />
                  }
                  <text fontSize={11} dy={14} textAnchor={directionality === 'rtl' ? 'end' : 'start'}>
                    {`${getText(language, casesDeathsMetricKey === 'cases' ? 'casesOfCovid19Per100kPeople' : 'fatalitiesOfCovid19Per100kPeople')} ${getText(language, 'inLast14Days')}`}
                  </text>
                  {/* {scale > 0.7 ? (
                    <text fontSize={11} dy={14} x={summaryWidth} textAnchor={directionality === 'rtl' ? 'start' : 'end'}>
                      {getText(language, isWorld ? 'barSizesWorld' : 'barSizesUS')}
                    </text>
                  ) : null } */}
                  <g key='N/A' transform={`translate(${-minSegmentWidth * 2}, ${sectionHeight})`}>
                    <rect
                      width={minSegmentWidth}
                      height={groupHeight}
                      stroke='#E2E0E0'
                      fill='#ddd'
                    />
                    <text
                      transform={`translate(${minSegmentWidth / 2}, ${sectionHeight})`}
                      fill='black'
                      textAnchor='middle'
                      fontSize={10}
                      dy={10}
                    >
                      {getText(language, 'noData')}
                    </text>
                  </g>
                  {
                    summaryData.map(group => {
                      const groupWidth = getGroupWidth(group);
                      const thisOffset = xOffset;
                      xOffset += groupWidth;
                      return (
                        <g key={group.key} transform={`translate(${thisOffset}, ${sectionHeight})`}>
                          <rect
                            width={groupWidth}
                            height={groupHeight}
                            fill={group.key}
                            stroke={group.index === -1 ? '#E2E0E0' : group.key}
                          />
                          {
                            group.index === -1
                              ? null
                              : <line y2={sectionHeight} stroke='#E2E0E0' />
                          }
                          <text
                            transform={`translate(${0}, ${sectionHeight})`}
                            fill='black'
                            textAnchor='middle'
                            fontSize={10}
                            dy={10}
                          >
                            {group.label}
                          </text>
                        </g>
                      );
                    })
                  }
                </g>
              </g>
            </g>
          </svg>
        </div>
      );
    });
  }, [colorScale, casesDeathsMetricKey, props.counties, props.dateSet, props.regionsById]);
  const summary = summaryByDay[dateIndex];

  let tooltip = null
  const usMapRef = useRef();

  if (hoveredFips) {
    const id = hoveredFips.county.id
    const dataKey = props.dateSet[dateIndex].dateString

    const population = id in props.populations ? props.populations[id][populationKey] : null;
    const regionProperties = {
      perCapita: null,
      value: null,
      population: population,
      containsSubnational: false
    };

    const regionData = props.regionsById[id];
    if (regionData && population) {
      const totalValue = regionData.dataMap[dataKey] ? (regionData.dataMap[dataKey][casesDeathsMetricKey] || null) : null;
      const lastValue = regionData.dataMap[dataKey] ?  regionData.dataMap[dataKey][`new_${casesDeathsMetricKey}_14dayAverage`] : null ;
      regionProperties.perCapita = lastValue / population;
      regionProperties.value = lastValue;
      regionProperties.totalValue = totalValue;
      regionProperties.population = population;
      regionProperties.containsSubnational = regionData.containsSubnational ? true : false;
      if (isNaN(regionProperties.perCapita)) {
        console.warn('isNaN(regionProperties.perCapita)');
        regionProperties.perCapita = null;
      }
      if (isNaN(regionProperties.totalValue)) {
        regionProperties.totalValue = null;
      }
    }
    tooltip =  (
      <Tooltip
        hoveredCounty={hoveredFips}
        // properties={regionProperties[hoveredFips.county.id]}
        properties={regionProperties}
        usMapRef={usMapRef}
        populationKey={populationKey}
        colorScale={colorScale}
        {...props}
        metricKey={casesDeathsMetricKey}
        dataKey={dataKey}
        instruction={selectedStateAbbr ? 'Click to zoom out' : 'Click to show state-level view'}
      />
    )
  }


 return (
    <div className={classnames({embed: embedParams !== null})}>
      <TimelapseControls
        setPaused={setPaused}
        paused={paused}
        setDateIndex={setDateIndex}
        dateIndex={dateIndex}
        casesDeathsMetricKey={casesDeathsMetricKey}
        setCasesDeathsMetricKey={setCasesDeathsMetricKey}
        playbackSpeed={playbackSpeed}
        setPlaybackSpeed={setPlaybackSpeed}
        singleColumnMode={singleColumnMode}
        dateSliderWidth={dateSliderWidth}
        timelapseRegionKey={selectedStateAbbr ? selectedStateAbbr : 'US'}
        populationKey={populationKey}
        colorScale={colorScale}


        {...props}
      />
      <div className={classnames('Timelapse', {singleColumnMode, embed: embedParams !== null})} ref={usMapRef}>
        <div className='column main' style={{ width: mainColumnWidth}}>


          <div ref={mapContainerRef} className='container' style={{ height: mapContainerHeight }}>
            {selectedStateId ? (
              <div className='selectedStateName'>{props.regionDataByKey[selectedStateAbbr].state}</div>
            ) : null}
            <div
              className={`viewUSMapButton ${selectedStateId ? 'button' : ''}`}
              onClick={() => {
                history.replace('/timelapse')
              }}
            >
              <span>
                {selectedStateId ? 'View US Map' : null}
              </span>
            </div>
            <div
              className='centerContainer'
              style={{
                width: width * scale,
                height: height * scale,
              }}
            >
              <svg width={width} height={height} style={{ transform: `scale(${scale})` }}>
                {countyPaths}
              </svg>
              <canvas width={width} height={height} ref={canvasRef} style={{ transform: `scale(${scale})` }}>
              </canvas>
            </div>
            {tooltip}
          </div>
          <div
            style={{
              position: 'relative',
              display: 'flex',
              height: `${60 * scale}px`,
              width: width * scale,
              margin: '0 auto',
            }}
          >
            <div className='summaryContainer' style={{ width, transform: `scale(${scale})` }}>
              {summary}
            </div>
          </div>
        </div>
        <div className='column right' style={{ width: rightColumnWidthWithPadding }}>

          <div className='exportEmbedButtonContainer' style={{ display: embedParams !== null ? 'none' : null }}>
            <div className={classnames('exportEmbedButton', props.className)} onClick={() => setShowExport(d => !d)}>
              <img src={exportEmbedIcon} alt='' />
              <div className='buttonLabel'>{getText(language, 'exportEmbed')}</div>
            </div>
          </div>

          <Export
            showExport={showExport}
            setShowExport={setShowExport}
            regionType={'timelapse'}
            language={'en'}
            directionality={'ltr'}
            isWorld={false}
          />
          <div className='rightTitle'>
            Showing {casesDeathsMetricKey === 'cases' ? 'cases' : 'fatalities'} as of{' '}
            <span style={{ fontWeight: 'bold' }}>{formatDateLabel(props.dateSet[dateIndex].date)}</span>
          </div>
          <div className='infoSection' style={{ height: barGraphHeight + 24 }}>
            <div className='label bold graphTitle'>
              Total Since March
            </div>
            <TimelapseBarGraph
              contentWidth={rightColumnContentWidth}
              contentHeight={barGraphHeight}
              data={statData.data}
              selectedMetric={selectedMetric}
              population={usPopulation}
              barColor={barGraphColor}
              language={language}
            />
          </div>
          <div className='infoSection' style={{ height: barGraphHeight + 24 }}>
            <div className='label bold graphTitle'>
              Last 14 days
            </div>
            <TimelapseBarGraph
              contentWidth={rightColumnContentWidth}
              contentHeight={barGraphHeight}
              data={statData.twoWeeks}
              selectedMetric={selectedMetric}
              population={usPopulation}
              barColor={barGraphColor}
              language={language}
              nonZeroBaseline={true}
            />
          </div>
          <div className='infoSection'>
            {stats.map(stat => {
              return <div className='stat' key={stat.label}>
                <div className='label'>{stat.label}</div>
                <div className='label bold'>{stat.value.toLocaleString()}</div>
              </div>
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function TimelapseLoader(props) {

  const { timelapseHistoryLoaded, loadTimelapseHistory, timelapseSubregionHistoryLoaded } = props

  const params = useParams()
  let selectedStateId = null
  let selectedStateAbbr = null
  if (params.selectedStateAbbr) {
    const data = props.regionDataByKey[params.selectedStateAbbr]
    if (data && data.stateAbbr === params.selectedStateAbbr) {
      selectedStateId = data.fips
      selectedStateAbbr = params.selectedStateAbbr
    }
  }

  // needs loading if a state is selected and neither subregion or full country history is loaded
  // of if no state selected and entire history isn't loaded
  const needsLoading = (selectedStateId && !timelapseSubregionHistoryLoaded && !timelapseHistoryLoaded) || (!selectedStateId && !timelapseHistoryLoaded)
  useEffect(() => {
    if (needsLoading) {
      loadTimelapseHistory(selectedStateId)
    }
  }, [needsLoading, loadTimelapseHistory, selectedStateId])


  return !needsLoading ?
    <Timelapse {...props} selectedStateAbbr={selectedStateAbbr} selectedStateId={selectedStateId}/> :
    <div className='timelapseLoader'>
      <Spinner name="ball-spin-fade-loader" />
    </div>
}
